// Buying several phones from one seller at once, straight from Phone check: the phones checked on
// SICKW are picked there, each one's model comes from its check, and here each gets its grade, the
// price paid, and where it goes (inventory, or a wholesale buyer). The seller is paid the total once
// and gets one receipt listing every phone.
//
// Bulk buy (Buy a phone → Bulk buy) is the same form without the checks: the IMEIs are typed or
// pasted in, each phone gets what it cost and what it sells for wholesale (or goes to inventory),
// and the batch page prints a wholesale sticker for every phone (cost and sold price on it).
import { useEffect, useMemo, useRef, useState } from 'react';
import { get, post, qs } from '../lib/api';
import { navigate, useDebounced, useLoad } from '../lib/hooks';
import { useSession } from '../lib/session';
import { formatCents } from '../../shared/money';
import { formatDateTime, formatPhone, isValidImei, normalizePhone } from '../../shared/format';
import { BUY_PAYOUTS, PAYOUT_LABELS, isAppPayout, sellerPayoutLabel, type ImeiCheckSection, type Payout, type WholesalePay } from '../../shared/buyback';
import type { LicenseData } from '../../shared/license';
import { Badge, Button, Cost, Empty, Field, MoneyInput, Profit, Spinner, useAction, useToast, type ApprovalInput } from '../ui/kit';
import { Icon } from '../ui/icons';
import { Barcode, usePrint } from '../ui/print';
import { useCloudPrint } from '../lib/printing';
import { ReceiptMark } from '../ui/legal';
import { SignaturePad } from '../ui/signature';
import { photoToDataUrl, pickPhoto } from '../lib/image';
import { scanThumbprint } from '../lib/fingerprint';
import { useLicenseScanner } from '../lib/license-scan';
import { ModelPhoto } from '../ui/photo';
import { ReceiptHeader, ReceiptPolicies, SiteLine, type StoreInfo } from './Receipt';
import { PAY_NOTE, carrierText } from './Buyback';
import { suggestFromCheck } from '../../shared/sickw-phone';
import { LicenseSummary, ScanIdSheet } from './ScanId';
import { usePhoneCheck } from './PhoneCheck';
import { printPriceStickers, type StickerPhone } from './PriceSticker';
import { WholesalePayPicker, wholesalePaidText } from './Wholesale';

interface CheckLine { label: string; value: string }
interface Check {
  id: string; imei: string; service: { id: string; name: string }; status: 'ok' | 'error'; verdict: string; lines?: CheckLine[]; created_at: number;
}
interface Catalog { brands: Record<string, Record<string, string[]>>; carriers: string[]; conditions: string[]; grade_help?: Record<string, string> }

type Dest = 'inventory' | 'wholesale';
interface Row {
  /** The check it came from (Phone check), or null for a phone typed into a bulk buy. */
  key: string; code: string; check: Check | null;
  brand: string; model: string; storage: string; color: string; carrier: string; locked: boolean;
  condition: string; offer: number | null; dest: Dest; sell: number | null; ws: number | null;
  /** Battery health % (digits), for the price sticker. */
  battery: string;
}

const VERDICT: Record<string, { label: string; tone: 'green' | 'red' | 'amber' }> = {
  clean: { label: 'Clean', tone: 'green' },
  blocked: { label: 'Blacklisted / lost', tone: 'red' },
  fmi_on: { label: 'iCloud ON', tone: 'red' },
  error: { label: 'Check didn’t work', tone: 'amber' },
};

const HINTS: [RegExp, string][] = [[/iphone|ipad|apple|macbook/i, 'Apple'], [/galaxy|samsung|\bsm-/i, 'Samsung'], [/pixel|google/i, 'Google'], [/motorola|moto\b/i, 'Motorola']];

/** What a check says the phone is: brand, model, storage, color and carrier, matched to the website's list. */
function fromCheck(check: Check, cat: Catalog | null): Pick<Row, 'brand' | 'model' | 'storage' | 'color' | 'carrier' | 'locked'> {
  const lines = check.lines ?? [];
  const blank = { brand: 'Apple', model: '', storage: '', color: '', carrier: 'Unlocked', locked: false };
  const text = `${check.service.name} ${lines.map((l) => `${l.label} ${l.value}`).join(' ')}`;
  const hinted = HINTS.find(([re]) => re.test(text))?.[1];
  if (check.status !== 'ok' || !cat) return { ...blank, brand: hinted ?? blank.brand };
  const sections: ImeiCheckSection[] = [{ service: check.service.id, title: check.service.name, ok: true, fields: lines.map((l) => [l.label, l.value]), error: null }];
  const brands = Object.keys(cat.brands ?? {});
  const order = hinted ? [hinted, ...brands.filter((b) => b !== hinted)] : brands;
  for (const b of order) {
    const models = Object.keys(cat.brands[b] ?? {});
    const s = suggestFromCheck(sections, models, (m) => cat.brands[b]?.[m] ?? [], cat.carriers ?? []);
    if (s?.model) return { brand: b, model: s.model, storage: s.storage ?? '', color: s.color ?? '', carrier: s.carrier ?? 'Unlocked', locked: !!s.locked };
  }
  // No model on the website's list: keep what SICKW calls it, to fix by hand.
  const named = lines.find((l) => /^model( name| desc(ription)?)?$/i.test(l.label))?.value ?? '';
  return { ...blank, brand: hinted ?? blank.brand, model: named.replace(/\s*[[(].*$/, '').slice(0, 60) };
}

const newRequestId = () => `bat_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
let rowSeq = 0;
/** An empty row for a phone typed into a bulk buy: wholesale unless bought phones are held first. */
function blankRow(code: string, dest: Dest): Row {
  return { key: `row_${++rowSeq}`, code, check: null, brand: 'Apple', model: '', storage: '', color: '', carrier: 'Unlocked', locked: false, condition: '', offer: null, dest, sell: null, ws: null, battery: '' };
}
/** The IMEIs and serial numbers in a pasted list, one per line (or comma-separated): 15 digits that check out, or 5+ letters and digits. */
export function codesFromText(text: string): string[] {
  const out: string[] = [];
  for (const raw of text.split(/[\s,;]+/)) {
    const c = raw.replace(/[^0-9A-Za-z-]/g, '').toUpperCase();
    if (!c || out.includes(c)) continue;
    if (/^\d{15}$/.test(c) ? isValidImei(c) : c.length >= 5 && c.length <= 30) out.push(c);
  }
  return out.slice(0, 30);
}
/** What's wrong with a typed IMEI or serial, or null when it's fine. */
export function codeProblem(code: string): string | null {
  const c = code.trim();
  if (!c) return 'Type its IMEI or serial';
  if (/^\d+$/.test(c)) return c.length === 15 ? (isValidImei(c) ? null : 'That IMEI doesn’t check out') : 'An IMEI is 15 digits';
  return c.length >= 5 ? null : 'Too short for a serial';
}

export function BuyManyScreen({ query }: { query: URLSearchParams }) {
  const ids = useMemo(() => (query.get('checks') ?? '').split(',').map((x) => x.trim()).filter(Boolean).slice(0, 30), [query]);
  const checks = useLoad<Check[]>(() => Promise.all(ids.map((id) => get<Check>(`/api/phone-checks/${id}`))), [ids.join(',')]);
  const cat = useLoad<Catalog>(() => get('/api/buyback/catalog'), []);
  if (!ids.length) return <div className="page"><Empty icon="phone" title="No phones picked">Open <strong>Phone check</strong>, tick the phones you checked, and press <strong>Buy these</strong>.</Empty></div>;
  if (checks.error) return <div className="page"><Empty icon="alert" title="Those checks couldn’t be loaded.">{checks.error.message}</Empty></div>;
  if (!checks.data || (cat.loading && !cat.data && !cat.error)) return <div className="page"><Spinner label="Loading the phones" /></div>;
  return <BuyManyForm checks={checks.data} cat={cat.data ?? null} />;
}

/** Buy a phone → Bulk buy: the phones are typed in (IMEI, model, what you pay, what it sells for wholesale), no check needed first. */
export function BulkBuyScreen() {
  const cat = useLoad<Catalog>(() => get('/api/buyback/catalog'), []);
  if (cat.loading && !cat.data && !cat.error) return <div className="page"><Spinner label="Loading" /></div>;
  return <BuyManyForm checks={[]} cat={cat.data ?? null} bulk />;
}

function BuyManyForm({ checks, cat, bulk = false }: { checks: Check[]; cat: Catalog | null; bulk?: boolean }) {
  const { me, can } = useSession();
  // Wholesale profit is the owner's to see (it's worked out from what was paid and the wholesale price).
  const seesProfit = can('profit.view');
  const toast = useToast();
  const pc = usePhoneCheck();
  const { run, busy } = useAction();
  const requestId = useRef(newRequestId());
  // Bought phones stay in the store for the hold days (Settings → Buy & trade-in), wholesale too:
  // the hold is so the police can find a stolen phone, and a wholesale buyer would take it away.
  const holdDays = Math.max(0, Math.floor(Number(me?.settings?.buyback_hold_days ?? 0) || 0));
  // A bulk buy is for wholesale unless the hold says the phones stay first.
  const bulkDest: Dest = holdDays > 0 ? 'inventory' : 'wholesale';
  // One row per phone (the newest check of each IMEI); a bulk buy starts with three empty rows.
  const [rows, setRows] = useState<Row[]>(() => {
    if (bulk) return [blankRow('', bulkDest), blankRow('', bulkDest), blankRow('', bulkDest)];
    const seen = new Set<string>();
    return [...checks].sort((a, b) => b.created_at - a.created_at).filter((c) => (seen.has(c.imei) ? false : (seen.add(c.imei), true))).map((c) => ({
      key: c.imei, code: c.imei, check: c, ...fromCheck(c, cat), condition: '', offer: null, dest: 'inventory' as Dest, sell: null, ws: null, battery: '',
    }));
  });
  const set = (key: string, patch: Partial<Row>) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  // Bulk buy: a pasted list of IMEIs fills the empty rows first, then adds rows (30 phones at most).
  const [pasted, setPasted] = useState('');
  const addCodes = () => {
    const codes = codesFromText(pasted).filter((c) => !rows.some((r) => r.code.trim().toUpperCase() === c));
    if (!codes.length) { toast('No IMEI or serial number found in that list.', 'error'); return; }
    setRows((rs) => {
      const next = rs.map((r) => ({ ...r }));
      const queue = [...codes];
      for (const r of next) { if (!r.code.trim() && queue.length) r.code = queue.shift()!; }
      while (queue.length && next.length < 30) next.push(blankRow(queue.shift()!, bulkDest));
      return next;
    });
    setPasted('');
    toast(`Added ${codes.length} phone${codes.length === 1 ? '' : 's'}.`, 'good');
  };

  // The seller: picked from the customers on file, or typed (added when the purchase is saved).
  const [custName, setCustName] = useState('');
  const [custPhone, setCustPhone] = useState('');
  const [known, setKnown] = useState<{ id: string; name: string; phone: string | null } | null>(null);
  const [nameOpen, setNameOpen] = useState(false);
  const [nameHits, setNameHits] = useState<any[]>([]);
  const [scanOpen, setScanOpen] = useState(false);
  const [scanned, setScanned] = useState<LicenseData | null>(null);
  const [age, setAge] = useState(false);
  // Paying, where the wholesale phones go, and the seller's signature and thumbprint.
  const [payout, setPayout] = useState<Payout>('blue_bag');
  const [payoutRef, setPayoutRef] = useState('');
  const [buyer, setBuyer] = useState('');
  const [wsPaidBy, setWsPaidBy] = useState<WholesalePay | ''>('');
  const [wsPaidRef, setWsPaidRef] = useState('');
  const [note, setNote] = useState('');
  const [signature, setSignature] = useState<string | null>(null);
  const [thumb, setThumb] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [scanErr, setScanErr] = useState<string | null>(null);
  const buyers = useLoad<{ buyers: string[] }>(() => get('/api/wholesale/buyers'), []);

  const phone10 = normalizePhone(custPhone);
  useEffect(() => {
    let live = true;
    if (!phone10) { setKnown(null); return; }
    if (known?.phone === phone10) return;
    get<any[]>(`/api/customers${qs({ q: phone10, limit: 5 })}`)
      .then((hits) => {
        if (!live) return;
        const hit = hits.find((h) => h.phone === phone10);
        setKnown(hit ? { id: hit.id, name: hit.name, phone: hit.phone } : null);
        if (hit) setCustName((n) => (n.trim() ? n : hit.name));
      })
      .catch(() => undefined);
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone10]);
  const dn = useDebounced(custName.trim(), 200);
  useEffect(() => {
    let live = true;
    if (dn.length < 2 || (known && known.name.toLowerCase() === dn.toLowerCase())) { setNameHits([]); return; }
    get<any[]>(`/api/customers${qs({ q: dn, limit: 6 })}`).then((h) => live && setNameHits(h)).catch(() => undefined);
    return () => { live = false; };
  }, [dn, known]);
  const choose = (h: any) => {
    setNameOpen(false);
    setNameHits([]);
    setKnown({ id: h.id, name: h.name, phone: h.phone });
    setCustName(h.name);
    if (h.phone) setCustPhone(formatPhone(h.phone));
  };
  const applyLicense = (d: LicenseData) => {
    setScanOpen(false);
    setScanned(d);
    setCustName((n) => n.trim() || d.name);
    toast(`Read ${d.name}’s ${d.kind === 'ID' ? 'ID' : 'license'}.`, 'good');
  };
  useLicenseScanner(applyLicense, () => toast('That license didn’t read. Scan it again, or press Scan ID.', 'error'), !scanOpen);

  const scanThumb = async () => {
    setScanning(true);
    setScanErr(null);
    try {
      const r = await scanThumbprint({ license: me?.settings?.fingerprint_license || undefined });
      setThumb(r.dataUrl);
      toast('Thumbprint scanned.', 'good');
    } catch (e) {
      setScanErr(e instanceof Error ? e.message : 'The scanner didn’t work.');
    } finally {
      setScanning(false);
    }
  };
  const uploadThumb = async () => {
    const f = await pickPhoto();
    if (!f) return;
    try {
      setThumb(await photoToDataUrl(f, 1000, 0.92));
      setScanErr(null);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'That picture can’t be used.', 'error');
    }
  };

  const brands = useMemo(() => (Object.keys(cat?.brands ?? {}).length ? Object.keys(cat!.brands) : ['Apple', 'Samsung', 'Google']), [cat]);
  const carriers = cat?.carriers?.length ? cat.carriers : ['Unlocked'];
  const grades = cat?.conditions ?? [];
  const total = rows.reduce((s, r) => s + (r.offer ?? 0), 0);
  const wholesale = rows.filter((r) => r.dest === 'wholesale');
  const wsTotal = wholesale.reduce((s, r) => s + (r.ws ?? 0), 0);
  const wsPaid = wholesale.reduce((s, r) => s + (r.offer ?? 0), 0);
  const many = rows.length !== 1;
  const codes = rows.map((r) => r.code.trim().toUpperCase());
  const missing = [
    custName.trim().length < 2 && 'seller’s name',
    !phone10 && 'phone number',
    bulk && rows.some((r) => codeProblem(r.code)) && 'an IMEI or serial on every phone',
    bulk && codes.some((c, i) => c && codes.indexOf(c) !== i) && 'no phone twice',
    rows.some((r) => !r.model.trim()) && 'every model',
    rows.some((r) => !r.condition) && 'every grade',
    rows.some((r) => !(r.offer && r.offer > 0)) && 'what you pay for each',
    wholesale.some((r) => !(r.ws && r.ws > 0)) && 'each wholesale price',
    wholesale.length > 0 && buyer.trim().length < 2 && 'the wholesale buyer',
    wholesale.length > 0 && (!wsPaidBy || (wsPaidBy === 'other' && !wsPaidRef.trim())) && 'how the wholesale buyer paid',
    wholesale.length > 0 && holdDays > 0 && 'every phone in Inventory (wholesale waits for the hold)',
    rows.some((r) => r.dest === 'inventory' && r.battery && (Number(r.battery) < 1 || Number(r.battery) > 100)) && 'battery health 1 to 100',
  ].filter(Boolean) as string[];

  const save = async () => {
    if (missing.length || !rows.length) return;
    const body = {
      requestId: requestId.current,
      customerId: known && known.name.toLowerCase() === custName.trim().toLowerCase() ? known.id : null,
      customer: { name: custName.trim(), phone: custPhone },
      payoutMethod: payout,
      payoutRef: isAppPayout(payout) ? payoutRef.trim() || null : null,
      seller: scanned ? { name: scanned.name, address: scanned.address || null, city: scanned.city || null, state: scanned.addressState || null, zip: scanned.zip || null, dob: scanned.dob } : null,
      id: scanned ? { type: scanned.kind === 'ID' ? 'State ID' : 'Driver license', number: scanned.number, state: scanned.state || null, expires: scanned.expires } : null,
      ageConfirmed: !!scanned && age,
      signature,
      thumbprint: thumb,
      wholesaleBuyer: wholesale.length ? buyer.trim() : null,
      wholesalePaidBy: wholesale.length ? wsPaidBy || null : null,
      wholesalePaidRef: wholesale.length ? wsPaidRef.trim() || null : null,
      note: note.trim() || null,
      items: rows.map((r) => ({
        brand: r.brand, model: r.model.trim(), storage: r.storage || null, color: r.color.trim() || null, carrier: r.carrier, locked: r.carrier !== 'Unlocked' && r.locked,
        imei: /^\d{15}$/.test(r.code.trim()) ? r.code.trim() : null, serial: /^\d{15}$/.test(r.code.trim()) ? null : r.code.trim().toUpperCase(),
        condition: r.condition, offerCents: r.offer, destination: r.dest,
        sellPriceCents: r.dest === 'inventory' ? r.sell : null, wholesalePriceCents: r.dest === 'wholesale' ? r.ws : null,
        batteryHealth: r.dest === 'inventory' && r.battery ? Number(r.battery) : null,
      })),
    };
    const r = await run((approval?: ApprovalInput) => post<{ id: string }>('/api/buybacks/batch', { ...body, approval }));
    if (r) {
      toast(`Bought ${rows.length} phone${many ? 's' : ''}. The receipt is printing.`, 'good');
      navigate(`/buybacks/batch/${r.id}?saved=1`);
    }
  };
  const addRow = () => setRows((rs) => (rs.length < 30 ? [...rs, blankRow('', bulkDest)] : rs));

  const amount = formatCents(total);
  const payButton = payout === 'blue_bag' ? `Pay ${amount} from blue bag` : payout === 'cash' ? `Pay ${amount} cash from drawer` : `Save ${amount} ${PAYOUT_LABELS[payout]} payment`;
  return (
    <div className="page" style={{ paddingBottom: 120 }}>
      <div className="page-head">
        <h1>{bulk ? 'Bulk buy' : many ? `Buy ${rows.length} phones` : 'Buy this phone'}</h1>
        <div className="row-wrap">
          {bulk ? <Button variant="ghost" icon="phoneCash" onClick={() => navigate('/buy')}>Buy one phone</Button> : null}
          <Button variant="ghost" icon="search" onClick={() => pc.open()}>Phone check</Button>
          <Button variant="ghost" icon="list" onClick={() => navigate('/buybacks')}>Phones bought</Button>
        </div>
      </div>
      {bulk ? <p className="small muted" style={{ margin: 0 }}>Type or paste the IMEIs, then each phone’s model, grade, what you pay and what it sells for wholesale (or send it to Inventory). The phones are bought, paid for once and listed on one receipt; the batch page prints a wholesale sticker for each one with its cost and sold price.</p> : null}
      {!me?.register ? <div className="banner warn"><Icon.alert /><div>Buying phones needs the store tablet.</div></div> : null}

      <section className="card stack">
        <div className="spread" style={{ flexWrap: 'wrap', rowGap: 4 }}>
          <h2 style={{ whiteSpace: 'nowrap' }}>1. Seller</h2>
          <Button size="sm" variant="ghost" icon="idCard" onClick={() => setScanOpen(true)}>Scan ID</Button>
        </div>
        <div className="form-grid">
          <Field label="Seller’s name" hint="search the customers on file, or type a new one">
            <div className="cust-search">
              <input className="input input-lg" value={custName} onChange={(e) => { setCustName(e.target.value); setNameOpen(true); }}
                onFocus={() => setNameOpen(true)} onBlur={() => setTimeout(() => setNameOpen(false), 150)} placeholder="Search customer name or phone" autoComplete="off" aria-label="Seller’s name" role="combobox" aria-expanded={nameOpen && nameHits.length > 0} />
              {nameOpen && nameHits.length ? (
                <div className="cust-drop" role="listbox" aria-label="Customers on file">
                  {nameHits.map((h) => (
                    <button key={h.id} type="button" role="option" aria-selected={false} className="cust-hit" onMouseDown={(e) => e.preventDefault()} onClick={() => choose(h)}>
                      <span className="avatar avatar-sm">{h.name.slice(0, 1)}</span>
                      <span className="grow"><span className="t">{h.name}</span><span className="s">{[formatPhone(h.phone), h.email].filter(Boolean).join(' · ') || 'No contact info'}</span></span>
                    </button>
                  ))}
                  <div className="cust-none small muted">Not them? Keep typing: a new customer is added when you save.</div>
                </div>
              ) : null}
            </div>
          </Field>
          <Field label="Phone number" hint={custPhone.trim() && !phone10 ? 'needs 10 digits' : undefined}>
            <input className="input input-lg" inputMode="tel" value={custPhone} onChange={(e) => setCustPhone(e.target.value)} onBlur={() => phone10 && setCustPhone(formatPhone(phone10))} placeholder="(510) 555-0123" autoComplete="off" aria-label="Phone number" />
          </Field>
        </div>
        {scanned ? <LicenseSummary d={scanned} adult /> : <p className="small muted" style={{ margin: 0 }}>Scan the barcode on the back of their license to keep their ID with every phone (California needs it for phones you buy).</p>}
        {scanned ? <label className="check"><input type="checkbox" checked={age} onChange={(e) => setAge(e.target.checked)} /> I checked the ID: it’s theirs and they’re 18 or older</label> : null}
        {known && phone10 ? <div className="customer-pill"><Icon.user /><div className="grow"><div className="strong">Returning customer: {known.name}</div><div className="small muted">{formatPhone(known.phone)}</div></div></div> : null}
      </section>

      <section className="card stack">
        <h2>2. {bulk ? `The phones (${rows.length})` : many ? `The ${rows.length} phones` : 'The phone'}</h2>
        {holdDays > 0 ? (
          <p className="small muted" style={{ margin: 0 }}>
            Wholesale is off here: bought phones are held {holdDays} day{holdDays === 1 ? '' : 's'} before they leave the store, wholesale too. Put them in Inventory, then sell them wholesale from their page when the hold ends.
          </p>
        ) : null}
        {bulk ? (
          <div className="bm-paste">
            <Field label="Paste a list of IMEIs" hint="one per line (or separated by commas); serial numbers work too. They fill the empty rows below, then add rows, up to 30 phones.">
              <textarea className="input" rows={3} value={pasted} onChange={(e) => setPasted(e.target.value)} placeholder={'356789012345678\n358123456789012'} aria-label="Paste a list of IMEIs" />
            </Field>
            <Button icon="plus" disabled={!codesFromText(pasted).length} onClick={addCodes}>Add {codesFromText(pasted).length || ''} phone{codesFromText(pasted).length === 1 ? '' : 's'}</Button>
          </div>
        ) : null}
        {rows.map((r, i) => {
          const models = Object.keys(cat?.brands?.[r.brand] ?? {});
          const storages = cat?.brands?.[r.brand]?.[r.model] ?? [];
          const v = r.check ? VERDICT[r.check.status === 'error' ? 'error' : r.check.verdict] : null;
          const codeBad = r.check ? null : codeProblem(r.code);
          const twice = !r.check && !!r.code.trim() && codes.indexOf(r.code.trim().toUpperCase()) !== i;
          return (
            <div key={r.key} className="bm-phone" aria-label={`Phone ${i + 1}`}>
              <div className="bm-head">
                <span className="thumb" style={{ width: 56, height: 56 }}><ModelPhoto brand={r.brand} model={r.model} color={r.color} size={56} /></span>
                <div className="grow">
                  <div className="strong">{i + 1}. {r.model.trim() ? `${r.brand === 'Apple' ? '' : `${r.brand} `}${r.model}${r.storage ? ` ${r.storage}` : ''}` : 'Which model?'}</div>
                  {r.check ? (
                    <div className="small muted num">{/^\d{15}$/.test(r.code) ? 'IMEI' : 'S/N'} {r.code} · {r.check.service.name}</div>
                  ) : (
                    <div className="bm-code">
                      <input
                        className={`input num${(codeBad && r.code.trim()) || twice ? ' input-bad' : ''}`} value={r.code} autoComplete="off" autoCapitalize="characters" placeholder="IMEI (15 digits) or serial"
                        aria-label={`Phone ${i + 1} IMEI or serial`} onChange={(e) => set(r.key, { code: e.target.value.replace(/[^0-9A-Za-z-]/g, '').slice(0, 30) })}
                      />
                      <span className={`small ${(codeBad && r.code.trim()) || twice ? 'error-text' : 'muted'}`}>{twice ? 'Already in the list' : r.code.trim() ? (codeBad ?? (/^\d{15}$/.test(r.code.trim()) ? 'IMEI' : 'Serial number')) : 'Dial *#06# on the phone for its IMEI'}</span>
                    </div>
                  )}
                </div>
                {v ? <Badge tone={v.tone}>{v.label}</Badge> : null}
                {rows.length > 1 ? <Button size="sm" variant="ghost" icon="close" aria-label={`Remove phone ${i + 1}`} onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))}>Remove</Button> : null}
              </div>
              <div className="form-grid">
                <Field label="Brand">
                  <select className="select" value={r.brand} onChange={(e) => set(r.key, { brand: e.target.value, model: '', storage: '' })} aria-label={`Phone ${i + 1} brand`}>
                    {(brands.includes(r.brand) ? brands : [r.brand, ...brands]).map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </Field>
                <Field label="Model">
                  <input className="input" list={`bm-models-${i}`} value={r.model} onChange={(e) => set(r.key, { model: e.target.value, storage: '' })} aria-label={`Phone ${i + 1} model`} placeholder={r.brand === 'Apple' ? 'e.g. iPhone 15' : 'Type the model'} />
                  <datalist id={`bm-models-${i}`}>{models.map((m) => <option key={m} value={m} />)}</datalist>
                </Field>
                <Field label="Storage">
                  {storages.length ? (
                    <select className="select" value={r.storage} onChange={(e) => set(r.key, { storage: e.target.value })} aria-label={`Phone ${i + 1} storage`}>
                      <option value="">Pick</option>
                      {storages.map((x) => <option key={x} value={x}>{x}</option>)}
                    </select>
                  ) : <input className="input" value={r.storage} onChange={(e) => set(r.key, { storage: e.target.value })} aria-label={`Phone ${i + 1} storage`} placeholder="e.g. 128GB" />}
                </Field>
                <Field label="Carrier">
                  <select className="select" value={r.carrier} onChange={(e) => set(r.key, { carrier: e.target.value, locked: e.target.value !== 'Unlocked' })} aria-label={`Phone ${i + 1} carrier`}>
                    {(carriers.includes(r.carrier) ? carriers : [r.carrier, ...carriers]).map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
              </div>
              {r.carrier !== 'Unlocked' ? <label className="check"><input type="checkbox" checked={r.locked} onChange={(e) => set(r.key, { locked: e.target.checked })} /> Locked to {r.carrier}</label> : null}
              <div className="chips" role="group" aria-label={`Phone ${i + 1} grade`}>
                {grades.map((g) => <button key={g} type="button" className="chip grade-chip" aria-pressed={r.condition === g} title={cat?.grade_help?.[g]} onClick={() => set(r.key, { condition: g })}>{g}</button>)}
              </div>
              <div className="bm-money">
                <Field label={bulk ? 'Bought for' : 'You pay'}>
                  <MoneyInput value={r.offer} onChange={(v) => set(r.key, { offer: v })} />
                </Field>
                <div className="stack-sm">
                  <span className="small strong">Then it goes to</span>
                  <div className="chips" role="group" aria-label={`Phone ${i + 1} goes to`}>
                    <button type="button" className="chip" aria-pressed={r.dest === 'inventory'} onClick={() => set(r.key, { dest: 'inventory' })}>Inventory</button>
                    <button
                      type="button" className="chip" aria-pressed={r.dest === 'wholesale'} disabled={holdDays > 0 && r.dest !== 'wholesale'}
                      title={holdDays > 0 ? `Bought phones are held ${holdDays} day${holdDays === 1 ? '' : 's'} first, wholesale too` : undefined}
                      style={holdDays > 0 && r.dest !== 'wholesale' ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
                      onClick={() => set(r.key, { dest: 'wholesale' })}
                    >
                      Wholesale
                    </button>
                  </div>
                </div>
                {r.dest === 'inventory' ? (
                  <>
                    <Field label="Sell for" hint="optional: puts it on sale now and prints its price sticker; empty keeps it in Testing">
                      <MoneyInput value={r.sell} onChange={(v) => set(r.key, { sell: v })} />
                    </Field>
                    <Field label="Battery health %" hint="prints on the sticker">
                      <input
                        className="input" inputMode="numeric" value={r.battery} placeholder="e.g. 89" aria-label={`Phone ${i + 1} battery health`}
                        onChange={(e) => set(r.key, { battery: e.target.value.replace(/\D/g, '').slice(0, 3) })}
                      />
                    </Field>
                  </>
                ) : (
                  <Field label={bulk ? 'Selling for (wholesale)' : 'Wholesale price'} hint={seesProfit && r.ws && r.offer ? <span className="pill-row"><Cost cents={r.offer} /><Profit cents={r.ws - r.offer} /></span> : 'what the buyer pays you'}>
                    <MoneyInput value={r.ws} onChange={(v) => set(r.key, { ws: v })} />
                  </Field>
                )}
              </div>
            </div>
          );
        })}
        {bulk ? <div className="row-wrap"><Button icon="plus" disabled={rows.length >= 30} onClick={addRow}>Add another phone</Button>{rows.length >= 30 ? <span className="small muted">30 phones at a time</span> : null}</div> : null}
      </section>

      {wholesale.length ? (
        <section className="card stack">
          <h2>Wholesale buyer</h2>
          <Field label="Who’s buying the wholesale phones" hint={<>{`${wholesale.length} phone${wholesale.length === 1 ? '' : 's'} · ${formatCents(wsTotal)}`}{seesProfit ? <> <span className="pill-row"><Cost cents={wsPaid} /><Profit cents={wsTotal - wsPaid} /></span></> : ' · the owner approves wholesale with their PIN'}</>}>
            <input className="input" list="bm-buyers" value={buyer} onChange={(e) => setBuyer(e.target.value)} placeholder="e.g. Bay Area Phone Wholesale" />
          </Field>
          <datalist id="bm-buyers">{(buyers.data?.buyers ?? []).map((b) => <option key={b} value={b} />)}</datalist>
          <WholesalePayPicker label={`How they paid the ${formatCents(wsTotal)}`} paidBy={wsPaidBy} paidRef={wsPaidRef} onPaidBy={setWsPaidBy} onPaidRef={setWsPaidRef} />
        </section>
      ) : null}

      <section className="card stack">
        <h2>3. Payment</h2>
        <div className="chips" aria-label="Pay with">
          {BUY_PAYOUTS.map((v) => <button key={v} type="button" className="chip" aria-pressed={payout === v} onClick={() => setPayout(v)}>{PAYOUT_LABELS[v]}</button>)}
        </div>
        <p className={`small ${payout === 'cash' ? 'strong' : 'muted'}`} style={{ margin: 0 }}>{PAY_NOTE[payout]}</p>
        {isAppPayout(payout) ? (
          <Field label={payout === 'zelle' ? 'Zelle confirmation number' : payout === 'cashapp' ? 'Cash App payment ID' : 'Apple Pay note'}>
            <input className="input" value={payoutRef} onChange={(e) => setPayoutRef(e.target.value)} />
          </Field>
        ) : null}
        <Field label="Note"><input className="input" value={note} onChange={(e) => setNote(e.target.value)} /></Field>
      </section>

      <section className="card stack">
        <h2>4. Seller signs and thumbprint <span className="muted small">(optional)</span></h2>
        <p className="small">{me?.settings?.buyback_terms}</p>
        <SignaturePad onChange={setSignature} initial={signature} label="Seller signs here" />
        <div className="thumb-row">
          <div className="thumb-slot" aria-label="Thumbprint">{thumb ? <img src={thumb} alt="Seller’s right thumbprint" /> : <span className="stack-sm" style={{ alignItems: 'center' }}><Icon.fingerprint /><span>Right thumb</span></span>}</div>
          <div className="stack-sm grow">
            <div className="strong">Right thumbprint</div>
            <div className="row-wrap">
              <Button variant={thumb ? 'secondary' : 'primary'} icon="fingerprint" busy={scanning} onClick={scanThumb}>{thumb ? 'Scan again' : 'Scan thumbprint'}</Button>
              <Button variant="ghost" icon="plus" onClick={uploadThumb}>Upload scan</Button>
              {thumb ? <Button variant="ghost" onClick={() => setThumb(null)}>Clear</Button> : null}
            </div>
            {scanErr ? <div className="error-text small" role="alert">{scanErr}</div> : null}
          </div>
        </div>
      </section>

      <div className="sticky-actions">
        <span className="grow small muted">
          {missing.length ? `Still needed: ${missing.join(', ')}` : <>{rows.length} phone{many ? 's' : ''} from {custName.trim()} · <strong className="num" style={{ color: 'var(--navy)', fontSize: 18 }}>{amount}</strong> · {PAYOUT_LABELS[payout]}{wholesale.length ? ` · ${wholesale.length} wholesale for ${formatCents(wsTotal)}` : ''}</>}
        </span>
        <Button size="lg" variant="primary" icon="check" busy={busy} disabled={!!missing.length || !me?.register || !rows.length} onClick={save}>{payButton}</Button>
      </div>
      {scanOpen ? <ScanIdSheet onClose={() => setScanOpen(false)} onScan={applyLicense} /> : null}
    </div>
  );
}

// ---------------------------------------------------------------- the purchase, after it's saved

export interface BatchPrint {
  id: string; number: string; first_number: string; created_at: number; location_name: string; employee_name: string;
  customer: { id: string | null; name: string; phone: string | null }; seller: { name: string; phone: string | null };
  payout_method: string; payout_ref: string | null; payout_label: string; total_cents: number | null; paid_cents: number | null; wholesale_buyer: string | null; note: string | null;
  phones: {
    id: string; number: string; status: string; brand: string; model: string; storage: string | null; color: string | null; carrier: string | null; locked: boolean;
    imei: string | null; serial: string | null; condition: string; offer_cents: number | null; imei_check_status: string;
    device: { id: string | null; status: string | null; price_cents: number | null; battery_health?: number | null; wholesale: { buyer: string; price_cents: number; paid_by?: string | null; paid_label?: string | null } | null };
  }[];
  // Print only.
  business_name?: string; logo_url?: string; website?: string; terms?: string; policies?: string | null; store?: StoreInfo;
  id_doc?: { type: string }; id_number_masked?: string; signature?: string | null;
}

const phoneTitle = (d: { brand: string; model: string; storage: string | null }) => `${d.brand === 'Apple' ? '' : `${d.brand} `}${d.model}${d.storage ? ` ${d.storage}` : ''}`;

/** Where a phone from this purchase is now. */
function whereNow(p: BatchPrint['phones'][number]): string {
  if (p.status !== 'completed') return 'Voided';
  if (p.device.wholesale) return `Sold wholesale to ${p.device.wholesale.buyer} for ${formatCents(p.device.wholesale.price_cents)} (${wholesalePaidText(p.device.wholesale)})`;
  if (p.device.status === 'in_stock') return `In inventory, on sale at ${formatCents(p.device.price_cents ?? 0)}`;
  if (p.device.status === 'sold') return 'Sold';
  if (p.device.status === 'on_hold') return 'In inventory, on hold';
  return 'In inventory (Testing)';
}

/**
 * The phones from this purchase sold wholesale, as wholesale stickers: what each cost (what the
 * seller was paid) and what it sold for. Only while the amounts paid are shown (the day of the
 * purchase at the counter; the owner always).
 */
function wholesaleStickers(p: BatchPrint): StickerPhone[] {
  return p.phones
    .filter((x) => x.status === 'completed' && x.device.wholesale && x.offer_cents !== null)
    .map((x) => ({
      brand: x.brand, model: x.model, storage: x.storage, color: x.color, carrier: x.carrier, locked: x.locked, imei: x.imei, serial: x.serial,
      battery_health: null, price_cents: x.device.wholesale!.price_cents, cost_cents: x.offer_cents,
    }));
}

/** The phones from this purchase that are on sale now, as price stickers. */
function onSaleStickers(p: BatchPrint): StickerPhone[] {
  return p.phones
    .filter((x) => x.status === 'completed' && (x.device.status === 'in_stock' || x.device.status === 'on_hold') && (x.device.price_cents ?? 0) > 0)
    .map((x) => ({
      brand: x.brand, model: x.model, storage: x.storage, color: x.color, carrier: x.carrier, locked: x.locked, imei: x.imei, serial: x.serial,
      battery_health: x.device.battery_health ?? null, price_cents: x.device.price_cents ?? 0,
    }));
}

export function BuybackBatchScreen({ id, query }: { id: string; query: URLSearchParams }) {
  const { me } = useSession();
  const print = usePrint();
  const cloud = useCloudPrint();
  const toast = useToast();
  const pc = usePhoneCheck();
  const b = useLoad<BatchPrint>(() => get(`/api/buybacks/batch/${id}`), [id]);
  const [justSaved] = useState(() => query.get('saved') === '1');
  const printed = useRef(false);
  useEffect(() => {
    if (justSaved) history.replaceState(null, '', `#/buybacks/batch/${id}`);
  }, [id, justSaved]);
  const printHere = async () => {
    try {
      const p = await get<BatchPrint>(`/api/buybacks/batch/${id}/print`);
      print(<BuybackBatchReceiptPaper p={p} />, 'thermal');
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Couldn’t print.', 'error');
    }
  };
  const printReceipt = () => cloud.printOut({ kind: 'buyback_batch_receipt', refId: id, title: `${b.data?.number ?? 'Purchase'} receipt`, browser: () => { printHere(); } });
  const printStickers = (p: BatchPrint) => printPriceStickers(print, onSaleStickers(p), me?.settings);
  const printWholesaleStickers = (p: BatchPrint) => printPriceStickers(print, wholesaleStickers(p), me?.settings);
  // Just bought: the seller's receipt prints once, then the price stickers for the phones put on
  // sale and a wholesale sticker (cost and sold price) for each phone sold wholesale.
  useEffect(() => {
    if (justSaved && b.data && !printed.current) {
      printed.current = true;
      printReceipt();
      printStickers(b.data);
      printWholesaleStickers(b.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [b.data]);
  if (b.error) return <div className="page"><Empty icon="alert" title="This purchase couldn’t be loaded.">{b.error.message}</Empty></div>;
  if (!b.data) return <div className="page"><Spinner /></div>;
  const x = b.data;
  const live = x.phones.filter((p) => p.status === 'completed');
  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>{live.length} phone{live.length === 1 ? '' : 's'} bought from {x.seller.name || x.customer.name}</h1>
          <div className="muted small">{x.number} · {formatDateTime(x.created_at)} · {x.location_name} · {x.employee_name}</div>
        </div>
        <div className="row-wrap">
          <Button variant="primary" icon="printer" onClick={printReceipt}>Print receipt</Button>
          {onSaleStickers(x).length ? <Button icon="tag" onClick={() => printStickers(x)}>Price sticker{onSaleStickers(x).length === 1 ? '' : 's'}</Button> : null}
          {wholesaleStickers(x).length ? <Button icon="tag" title="A 3 × 2 in sticker for each phone sold wholesale: the model, its IMEI, what it cost and what it sold for" onClick={() => printWholesaleStickers(x)}>Wholesale sticker{wholesaleStickers(x).length === 1 ? '' : 's'}</Button> : null}
          <Button icon="search" onClick={() => pc.open()}>Buy more</Button>
        </div>
      </div>
      {justSaved ? <div className="banner good" role="status"><Icon.check /><div>Bought and paid. Each phone has its own record below, and the seller’s receipt lists them all.</div></div> : null}
      <section className="card stack">
        <div className="spread" style={{ flexWrap: 'wrap', rowGap: 8 }}>
          <div>
            <div className="small muted">Paid to the seller</div>
            <div className="bm-total num">{x.total_cents === null ? '—' : formatCents(x.total_cents)}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="small muted">Paid by</div>
            <div className="strong">{x.payout_label}</div>
          </div>
        </div>
        {x.customer.id ? <a className="small" href={`#/customers/${x.customer.id}`}>{x.customer.name}{x.customer.phone ? ` · ${formatPhone(x.customer.phone)}` : ''}</a> : null}
        {x.note ? <p className="small" style={{ margin: 0 }}>Note: {x.note}</p> : null}
      </section>
      <section className="card">
        <div className="list">
          {x.phones.map((p) => (
            <a key={p.id} className="list-row" href={`#/buybacks/${p.id}`}>
              <span className="thumb" style={{ width: 44, height: 44 }}><ModelPhoto brand={p.brand} model={p.model} color={p.color} size={44} /></span>
              <span className="main-col">
                <span className="t" style={{ display: 'block' }}>{p.number} · {phoneTitle(p)}</span>
                <span className="s" style={{ display: 'block' }}>{p.imei ? `IMEI ${p.imei}` : `S/N ${p.serial}`} · Grade {p.condition} · {whereNow(p)}</span>
              </span>
              <span className={`num strong ${p.status === 'completed' ? '' : 'muted'}`}>{p.status === 'completed' ? (p.offer_cents === null ? '—' : formatCents(p.offer_cents)) : 'Voided'}</span>
              <Icon.chevronRight />
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}

/** The seller's receipt for several phones: every phone, the total and how it was paid. */
export function BuybackBatchReceiptPaper({ p }: { p: BatchPrint }) {
  const phone = formatPhone(p.seller.phone ?? p.customer.phone);
  const live = p.phones.filter((x) => x.status === 'completed');
  return (
    <div className="receipt">
      <ReceiptHeader logo={p.logo_url ?? ''} business={p.business_name ?? ''} store={p.store!} website={p.website} />
      <div className="c strong">BUY BACK RECEIPT</div>
      <div className="ticket-no">{live.length} PHONE{live.length === 1 ? '' : 'S'}</div>
      <div className="c strong">{p.number}</div>
      {p.first_number ? <div className="bar"><Barcode value={p.first_number} /></div> : null}
      <dl className="meta">
        <dt>Date</dt><dd>{formatDateTime(p.created_at)}</dd>
        <dt>Customer</dt><dd>{p.seller.name || p.customer.name}</dd>
        {phone ? <><dt>Phone</dt><dd>{phone}</dd></> : null}
        {p.id_number_masked ? <><dt>ID</dt><dd>{p.id_doc?.type} {p.id_number_masked}</dd></> : null}
        <dt>Served by</dt><dd>{p.employee_name}</dd>
      </dl>
      <hr />
      <table className="rtable">
        <thead><tr><th>PHONE</th><th className="r">PAID</th></tr></thead>
        <tbody>
          {p.phones.map((d) => (
            <tr key={d.id}>
              <td>
                <div className="strong">{phoneTitle(d)}</div>
                <div className="dim">{d.imei ? `IMEI ${d.imei}` : `S/N ${d.serial}`} · Grade {d.condition}</div>
                <div className="dim">{[d.number, d.color, carrierText(d)].filter(Boolean).join(' · ')}</div>
              </td>
              <td className="r">{d.status === 'completed' ? (d.offer_cents === null ? '-' : formatCents(d.offer_cents)) : 'VOIDED'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <hr />
      <div className="rl tot"><span>PAID TO YOU</span><span>{p.total_cents === null ? '-' : formatCents(p.total_cents)}</span></div>
      <div className="rl"><span>Paid by</span><span>{sellerPayoutLabel(p.payout_method, p.payout_ref)}</span></div>
      <hr />
      {p.signature ? (
        <>
          <div className="terms">{p.terms}</div>
          <img className="sig-print" src={p.signature} alt="Seller’s signature" />
          <div className="sign">Seller signature</div>
        </>
      ) : null}
      <div className="c strong pickup-note">Thank you! Keep this receipt.</div>
      <div className="c dim">Scan its barcode in STORE OS to open this purchase.</div>
      <ReceiptPolicies text={p.policies} />
      <SiteLine website={p.website} />
      <ReceiptMark />
    </div>
  );
}
